import { useContext, useState } from 'react';
import Amount, { IconText } from './Amount';
import { GameStateContext } from '../context/GameStateContext';
import CollapsedCard from './CollpasedCard';
import CardBack from './CardBack';
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');
const Leaders = require('@tta/engine/Constants/Leaders/Leaders');
const Wonders = require('@tta/engine/Constants/Wonders/Wonders');
const { CardVersion } = require('@tta/engine/Constants/CardVersions');

// The icon for each stat; production stats show the same icon followed by "/turn".
// Stats without an icon (military cards) are shown as text.
const StatKinds = {
    culture: 'culture', cultureProduction: 'culture',
    science: 'science', scienceProduction: 'science',
    food: 'food', foodProduction: 'food',
    resources: 'resources', resourceProduction: 'resources',
    strength: 'strength',
    happyFaces: 'happy faces',
    civilActions: 'CA', militaryActions: 'MA',
    yellowTokens: 'yellow tokens', blueTokens: 'blue tokens',
};
// Shown as text rather than an icon: a yellow token would read as yellow tokens gained
const TextLabels = { population: 'pop' };

// "+2 [resource]" in green, "-1 [food]/turn" in red
function Change({change})
{
    const kind = StatKinds[change.stat];
    return (
        <span className={`tta-change${change.change > 0 ? ' up' : change.change < 0 ? ' down' : ''}`}>
            {kind
                ? <><Amount value={change.change} kind={kind} signed/>{change.stat.endsWith('Production') && '/turn'}</>
                : <>{change.change > 0 ? '+' : ''}{change.change} {TextLabels[change.stat] ?? change.label}</>}
        </span>
    );
}

// One line per group of civilizations with the same changes: "All players", or their names in their colors
export function GroupChangeLine({group, colorOf})
{
    return (
        <div className="tta-change-line">
            <span className="tta-change-player">
                {group.allPlayers ? 'All players' : group.players.map((name, index) =>
                    <span key={name}>{index > 0 && ', '}<span style={{ color: colorOf(name) }}>{name}</span></span>)}
            </span>
            {group.changes.map(change => <Change key={change.stat} change={change}/>)}
        </div>
    );
}

// Losses (what a choice cost) before gains, otherwise keeping their order
export const costsFirst = (changes) => [...changes.filter(change => change.change < 0), ...changes.filter(change => change.change >= 0)];

// What a colony was paid with, as a table: a row each for units, tactic, cards and bonuses (only those worth something),
// then the total; each row its value, then exactly what it was (cards as in hand, hover for the card; James Cook's
// cards face down)
const PaymentRows = [
    { kind: 'units', label: 'Units' },
    { kind: 'tactic', label: 'Tactics' },
    { kind: 'cards', label: 'Cards' },
];

// What a row was, as columns of cards, one card per thing given up: a column per kind of unit (each unit under the
// one before); the Defense/Colonization cards together in one column (whatever their ages); James Cook's cards face
// down, side by side
const Columns = (row) => {
    const copies = (part) => Array.from({ length: part.count ?? 1 }, () => part);
    if (row.kind !== 'cards')
    {
        return row.parts.map(part => ({ parts: copies(part) }));
    }
    const shown = row.parts.filter(part => part.card).flatMap(copies);
    const backs = row.parts.filter(part => part.back).flatMap(copies);
    return [...(shown.length ? [{ parts: shown }] : []), ...(backs.length ? [{ parts: backs, across: true }] : [])];
};

function PaymentTable({choice, colorOf})
{
    const { sacrificed, bonuses, total } = choice.payment;
    const picture = (part) => part.card ? <CollapsedCard card={CardConverter(part.card)}/>
        : part.back ? <CardBack age={part.back} title={part.label}/>
        : <span>{part.label}</span>;
    const rows = [
        ...PaymentRows.map(row => ({ ...row, parts: sacrificed.filter(part => part.kind === row.kind) })),
        { kind: 'bonus', label: 'Bonus', parts: bonuses },
    ].map(row => ({ ...row, value: row.parts.reduce((sum, part) => sum + part.value, 0) })).filter(row => row.value !== 0);
    return (
        <div className="tta-change-line">
            <span className="tta-change-player" style={{ color: colorOf(choice.player) }}>{choice.player}</span>
            <table className="tta-payment-table">
                <thead><tr><th/><th>Value</th><th/></tr></thead>
                <tbody>
                    {rows.map(row =>
                        <tr key={row.kind}>
                            <th>{row.label}</th>
                            <td className="tta-payment-value">{row.value}</td>
                            <td><div className="tta-payment-columns">{Columns(row).map((column, index) =>
                                <div key={index} className={`tta-payment-column${column.across ? ' across' : ''}`}>{column.parts.map((part, at) =>
                                    <span key={at}>{picture(part)}</span>)}</div>)}</div></td>
                        </tr>)}
                    <tr className="tta-payment-total"><th>Total</th><td className="tta-payment-value">{total}</td><td/></tr>
                </tbody>
            </table>
        </div>
    );
}

// The card a recap entry is about (an event, aggression, war, pact, leader): as in hand, hover for the card; just its
// name for older entries
// A new age, under its heading: what each civilization lost and anything else that came with it, a line each, then the
// age's leaders and wonders (in a mix revealed age by age) as cards, hover for the card, each in the version the game's
// setup has
export function NewAgeDetails({text, revealed, Setup})
{
    const notes = text.includes(': ') ? text.slice(text.indexOf(': ') + 2).split('; ') : [];
    // (a line begins with a capital, except a player's name, kept as it is so it shows in their color)
    const game = useContext(GameStateContext);
    const names = (game?.gameState?.playerStates ?? []).map(player => player.name).filter(name => name);
    const capitalized = (note) => names.some(name => note.startsWith(name)) ? note : note[0].toUpperCase() + note.slice(1);
    const card =(type, name) => {
        const data = { type, name, age: (type === 'Leader' ? Leaders : Wonders)[name]?.age, id: -1 };
        const version = Setup && CardVersion(Setup, data);
        return CardConverter(version ? { ...data, version } : data);
    };
    return (
        <div className="tta-new-age">
            {notes.map((note, index) => <div key={index}><IconText text={capitalized(note)}/></div>)}
            {revealed && [['Leader', 'Leaders', revealed.leaders], ['Wonder', 'Wonders', revealed.wonders]].map(([type, label, names]) =>
                <div key={type} className="tta-new-age-cards">
                    <span className="tta-muted">{label} this age:</span>
                    {names.map(name => <span key={name} className="tta-card-token"><CollapsedCard card={card(type, name)}/></span>)}
                </div>)}
        </div>
    );
}

export const EventCard = ({event}) => event.card
    ? <span className="tta-card-token"><CollapsedCard card={CardConverter(event.card)}/></span>
    : <strong>{event.name}</strong>;

// A pact's step: "Andrew offers [pact] to Dave: (its terms)", "Dave accepts [pact]", "Dave declines [pact]: …",
// "[pact] between Andrew and Matt cancelled due to new pact"
function PactLine({event, colorOf})
{
    const name = (who) => <span style={{ color: colorOf(who), fontWeight: 'bold' }}>{who}</span>;
    const when = <span className="tta-muted"> ({event.age}, round {event.round})</span>;
    const text = event.text ? <>: <IconText text={event.text}/></> : null;
    switch (event.pactStep)
    {
        case 'offer':
            return <div>{name(event.revealedBy)} offers <EventCard event={event}/> to {name(event.target)}{when}{text}</div>;
        case 'end':
            return <div><EventCard event={event}/> between {name(event.revealedBy)} and {name(event.target)} <IconText text={event.text}/>{when}</div>;
        default:
            return <div>{name(event.revealedBy)} {event.pactStep === 'accept' ? 'accepts' : 'declines'} <EventCard event={event}/>{when}{text}</div>;
    }
}

// A war in play: when it resolves, and how it would if it resolved now (kept up to date): "It will resolve at the start
// of Matt's next turn; current resolution: Andrew → 3 [culture] → Matt"
function WarResolution({war, names, colorOf})
{
    const name = (number) => <b style={{ color: colorOf(names[number]) }}>{names[number]}</b>;
    const { outcome } = war;
    return (
        <div className="tta-change-line tta-war-resolution">
            <span>It will resolve at the start of {name(war.aggressor)}'s next turn; current resolution:</span>
            {outcome.victor === null || outcome.victor === undefined
                ? <span className="tta-muted">a tie, nothing happens</span>
                : <span>{name(outcome.loser)} → <IconText text={outcome.takes}/> → {name(outcome.victor)}</span>}
        </div>
    );
}

// A colonization auction's bids as a grid: a column per player (in bidding order), a row per time round the table; each
// cell the bid, "pass", or "–" once the player has passed (blank: not asked, being the highest bidder). The winning bid
// is highlighted.
export function AuctionGrid({bids, bidders, colorOf})
{
    // Everyone who could bid, from the start (older reports: those who bid)
    const players = bidders ?? [...new Set(bids.map(bid => bid.player))];
    const rows = [];
    let row = {};
    bids.forEach(bid => {
        if (row[bid.player])
        {
            rows.push(row);
            row = {};
        }
        row[bid.player] = bid;
    });
    rows.push(row);
    const winning = [...bids].reverse().find(bid => bid.value?.bid !== undefined);
    const passed = new Set();
    return (
        <table className="tta-auction-grid">
            <thead>
                <tr>{players.map(player => <th key={player} style={{ color: colorOf(player) }}>{player}</th>)}</tr>
            </thead>
            <tbody>
                {rows.map((cells, index) => {
                    const shown = players.map(player => {
                        const bid = cells[player];
                        if (!bid) return { key: player, text: passed.has(player) ? '–' : '' };
                        if (bid.value?.pass) { passed.add(player); return { key: player, text: 'pass', pass: true }; }
                        return { key: player, text: bid.value?.bid, winning: bid === winning };
                    });
                    return <tr key={index}>{shown.map(cell =>
                        <td key={cell.key} className={cell.winning ? 'winning' : cell.pass ? 'pass' : ''}>{cell.text}</td>)}</tr>;
                })}
            </tbody>
        </table>
    );
}

// For an event players chose for: each player on one line, with what they chose and what changed for them (players
// whose numbers changed without a choice too, e.g. the target of an aggression), in the order they chose. A choice for
// what happens to others (International Negotiations) comes first, on a line of its own, so it doesn't read as the
// chooser's own change
function PlayerChoiceLines({event, colorOf})
{
    const apart = event.choices.filter(choice => choice.apart);
    const own = event.choices.filter(choice => !choice.apart);
    const players = [...new Set([...own.map(choice => choice.player), ...(event.changes ?? []).map(entry => entry.player)])];
    return [...apart.map(choice =>
        <div key={`apart${choice.decisionId}`} className="tta-change-line">
            <span className="tta-change-player" style={{ color: colorOf(choice.player) }}>{choice.player}</span>
            <span className="tta-choice-text tta-payment-part"><IconText text={choice.text}/></span>
        </div>),
    ...players.map(player =>
        <div key={player} className="tta-change-line">
            <span className="tta-change-player" style={{ color: colorOf(player) }}>{player}</span>
            {/* (a choice made for them at once has no decision: e.g. their only wonder crumbling) */}
            {own.filter(choice => choice.player === player).map((choice, index) =>
                <span key={choice.decisionId ?? `made${index}`} className="tta-choice-text tta-payment-part"><IconText text={choice.text}/>
                    {/* A colony: the territory as its card */}
                    {choice.territory && <CollapsedCard card={CardConverter(choice.territory)}/>}</span>)}
            {/* What it cost first, then what it gave (each in the usual stat order) */}
            {costsFirst((event.changes ?? []).find(entry => entry.player === player)?.changes ?? []).map(change => <Change key={change.stat} change={change}/>)}
        </div>)];
}

// What an event compared, so the math can be checked: one line per measure, highest first, each civilization's value
// with what cards add only for this (e.g. "Weakest: Matt 2 · Andrew 3 · Dave 4 (+3 Nostradamus)")
export function RankingLines({rankings, colorOf})
{
    return (rankings ?? []).map(ranking =>
        <div key={ranking.measure} className="tta-ranking-line">
            <span className="tta-muted">{ranking.label ?? ranking.measure}:</span>
            {ranking.entries.map((entry, index) => {
                return (
                    <span key={entry.player} className="tta-ranking-entry">
                        {index > 0 && <span className="tta-muted">·</span>}
                        <span style={{ color: colorOf(entry.player), fontWeight: 'bold' }}>{entry.player}</span>
                        {ranking.kind ? <Amount value={entry.value} kind={ranking.kind}/> : <b>{entry.value}</b>}
                        {entry.extras && <span className="tta-muted">({entry.extras.map(extra => `${extra.value >= 0 ? '+' : ''}${extra.value} ${extra.source}`).join(', ')})</span>}
                    </span>);
            })}
        </div>);
}

// What happened since the current player's last turn: the events revealed (with what each did to every
// civilization) and the net changes to each civilization. Hidden when there is nothing to report.
function SinceLastTurn({GameViewModel, PlayerColors})
{
    const [open, setOpen] = useState(true);
    const { player, hasPreviousTurn, sinceGameStart, events, changes } = GameViewModel.sinceLastTurn;
    const names = GameViewModel.playerViewModels.map(p => p.name);
    // Each war in play goes with its declaration; one declared before this recap begins is listed on its own
    const wars = GameViewModel.wars ?? [];
    const warOf = (event) => event.kind === 'war' && !event.resolved
        ? wars.find(war => war.card.name === event.name && names[war.aggressor] === event.revealedBy && names[war.defender] === event.target) : null;
    const unlisted = wars.filter(war => !events.some(event => warOf(event) === war));
    if (events.length === 0 && changes.length === 0 && unlisted.length === 0)
    {
        return null;
    }
    const colorOf = (name) => PlayerColors[GameViewModel.playerViewModels.findIndex(p => p.name === name)];

    return (
        <div className="tta tta-left tta-since-last-turn">
            <div className="tta-since-header" onClick={() => setOpen(!open)} title={open ? 'Hide' : 'Show'}>
                {open ? '▾' : '▸'} {hasPreviousTurn ? `Since your last turn, ${player}` : sinceGameStart ? `Since the game began, ${player}` : 'This turn'}
                {!open && <span className="tta-muted"> - {events.length} event{events.length === 1 ? '' : 's'}, {changes.reduce((sum, group) => sum + group.players.length, 0)} civilization(s) changed</span>}
            </div>
            {open &&
                // (in fixed places: what happened on the left, taking the room there is; the changes against the right edge)
                <div className="tta-since-columns">
                    <div className="tta-since-events">
                    {(events.length > 0 || unlisted.length > 0) &&
                        <div>
                            <div className="tta-since-title">{events.every(e => !e.kind && !e.target) ? 'Events revealed' : 'What happened'}</div>
                            {events.map((event, index) =>
                                <div key={index} className="tta-since-event">
                                    {event.kind === 'notice' ? (event.private
                                        ? <div><em><IconText text={event.text}/></em><span className="tta-muted"> (only you see this)</span></div>
                                        : event.newAge
                                        ? <div><strong>{event.name}</strong><span className="tta-muted"> (round {event.round})</span>
                                            <NewAgeDetails text={event.text} revealed={event.revealed} Setup={GameViewModel.setup}/></div>
                                        : <div><strong>{event.name}</strong><span className="tta-muted"> (round {event.round})</span>: <IconText text={event.text}/></div>) :
                                    event.pactStep ? <PactLine event={event} colorOf={colorOf}/> :
                                    <div>
                                        <span style={{ color: colorOf(event.revealedBy), fontWeight: 'bold' }}>{event.revealedBy}</span>
                                        {/* A leader's political action (Columbus colonizing): "Andrew played [Christopher Columbus] (round 2)", then the choice lines */}
                                        {/* (or what it did, its cards drawn: "Dave sacrifices [Columbus] to colonize [territory]") */}
                                        {event.kind === 'leader'
                                            ? <>{' '}{event.headline ? <IconText text={event.headline}/> : <>{`${event.verb ?? 'played'} `}<EventCard event={event}/></>}<span className="tta-muted"> (round {event.round})</span></>
                                            : <>
                                        {event.target
                                            ? <>{event.kind === 'war' ? (event.resolved ? "'s " : ' declared ') : ' played '}<EventCard event={event}/>{event.kind === 'war' ? ' on ' : ' against '}<span style={{ color: colorOf(event.target), fontWeight: 'bold' }}>{event.target}</span>{event.resolved && ' was resolved'}</>
                                            : <>{(event.preparedCulture > 0 || event.preparedNotes) && <span className="tta-muted"> (<IconText text={[...(event.preparedCulture > 0 ? [`+${event.preparedCulture} culture for preparing`] : []), ...(event.preparedNotes ?? [])].join('; ')}/>)</span>}{' revealed '}<EventCard event={event}/></>}
                                        <span className="tta-muted"> ({event.age}, round {event.round})</span>{event.text && <>: <IconText text={event.text}/></>}</>}
                                    </div>}
                                    {/* How it went, a line each under it (e.g. a pact it ended, then "Dave defends with …"); an
                                        offer's terms as bullets */}
                                    {(event.lines ?? []).map((line, index) =>
                                        <div key={`line${index}`} className="tta-report-line">{event.pactStep === 'offer' && '• '}<IconText text={line}/></div>)}
                                    <RankingLines rankings={event.rankings} colorOf={colorOf}/>
                                    {/* An event players chose for (e.g. Development of Civilization): one line per player, their choice
                                        and what changed; otherwise players with the same changes share a line */}
                                    {/* A colonization auction: its bids as a grid, then the payment and what changed */}
                                    {warOf(event) && <WarResolution war={warOf(event)} names={names} colorOf={colorOf}/>}
                                    {event.choices?.some(choice => choice.type === 'colonyBid') &&
                                        <AuctionGrid bids={event.choices.filter(choice => choice.type === 'colonyBid')} bidders={event.bidders} colorOf={colorOf}/>}
                                    {/* The winner's payment, and how it adds up */}
                                    {event.choices?.filter(choice => choice.payment?.sacrificed).map(choice =>
                                        <PaymentTable key={`pay${choice.decisionId}`} choice={choice} colorOf={colorOf}/>)}
                                    {event.choices?.length > 0
                                        ? <PlayerChoiceLines event={{ ...event, choices: event.choices.filter(choice => choice.type !== 'colonyBid') }} colorOf={colorOf}/>
                                        : event.groups.map(group => <GroupChangeLine key={group.players.join()} group={group} colorOf={colorOf}/>)}
                                </div>)}
                            {unlisted.map((war, index) =>
                                <div key={`war${index}`} className="tta-since-event">
                                    <div>
                                        <span style={{ color: colorOf(names[war.aggressor]), fontWeight: 'bold' }}>{names[war.aggressor]}</span>
                                        {' declared '}<EventCard event={{ card: war.card.cardData ?? war.card, name: war.card.name }}/>{' on '}
                                        <span style={{ color: colorOf(names[war.defender]), fontWeight: 'bold' }}>{names[war.defender]}</span>
                                        <span className="tta-muted"> (round {war.round})</span>
                                    </div>
                                    <WarResolution war={war} names={names} colorOf={colorOf}/>
                                </div>)}
                        </div>}
                    </div>
                    {changes.length > 0 &&
                        <div className="tta-since-changes">
                            <div className="tta-since-title">Changes since your last turn</div>
                            {changes.map(group =>
                                <GroupChangeLine key={group.players.join()} group={group} colorOf={colorOf}/>)}
                        </div>}
                </div>}
        </div>
    );
}

export default SinceLastTurn;